import { nil, NO, UIDialogView, UITextView, UIView, YES } from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHCPVInviteCountCache } from "../Custom components/RH/Utils_slop/RHCPVInviteCountCache"
import { RHButtons } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHInvitedCompaniesViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { InvitedCompany } from "../SocketClientFunctions"
import { CPVTreeDialogView } from "./CPVTreeDialogView"
import { InvitedCompaniesView } from "./InvitedCompaniesView"
import { ProcurementInvitationState } from "./ProcurementInvitationState"


export { TaxDebtTooltipView } from "./TaxDebtTooltipView"


export class InvitedCompaniesDialogView extends UIDialogView {

    readonly contentView = new UIView()
        .configuredWithObject({
            backgroundColor: RHColor.whiteColor,
            style: {
                borderRadius: "12px",
                boxShadow: "0 8px 40px rgba(0,0,0,0.18)"
            }
        })
        .addedAsSubviewToView(this.view)

    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({ text: "Osalema kutsutavad ettevõtted" })
        .addedAsSubviewToView(this.contentView)

    readonly closeButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "✕" },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this.dismiss()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly primaryCPVCodeTitleLabel = new UITextView()
        .configuredWithObject({
            text: "Peamine CPV-kood:",
            style: {
                fontWeight: "600",
                fontSize: "0.9em",
                color: RHColor.textSecondary.stringValue
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly primaryCPVCodeLabel = new UITextView()
        .configuredWithObject({
            text: "Valimata",
            style: {
                fontSize: "0.9em",
                color: RHColor.textSecondary.stringValue
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly sourceCodesLabel = new UITextView()
        .configuredWithObject({
            text: "Täiendavad CPV-allikad:",
            style: {
                fontWeight: "600",
                fontSize: "0.9em",
                color: RHColor.textSecondary.stringValue
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly invitedCountLabel = new UITextView()
        .configuredWithObject({
            text: "Kutsutud: 0",
            style: {
                fontWeight: "600",
                fontSize: "0.9em",
                color: RHColor.textSecondary.stringValue,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "right"
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly sourceCodesSummaryLabel = new UITextView()
        .configuredWithObject({
            text: "Täiendavaid CPV-allikaid pole valitud.",
            style: {
                fontSize: "0.9em",
                color: RHColor.textSecondary.stringValue,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly sourceCodesButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Vali täiendavad CPV-allikad" },
            variant: RHButtons.compactPrimaryVariant,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: {
                fontSize: RHButtons.compactPrimaryFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._openSourceCodesPicker()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly invitedCompaniesView = new InvitedCompaniesView()
        .addedAsSubviewToView(this.contentView)

    readonly sourceCodesTreeDialog = new CPVTreeDialogView()

    readonly confirmButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Kinnita ja välju" },
            variant: RHInvitedCompaniesViewTokens.dialogConfirmButtonVariant,
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this.dismiss()
                }
            }
        })
        .addedAsSubviewToView(this.contentView)

    readonly bottomSpacerView = new UIView()
        .configuredWithObject({ userInteractionEnabled: NO })
        .addedAsSubviewToView(this)

    // ─── State ────────────────────────────────────────────────────────────────

    _cpvCode?: string
    _onSaveConfirmed?: (
        invitedCompanies: InvitedCompany[],
        additionalSourceCPVCodes: string[]
    ) => void
    _loadSequence = 0
    _sourceReloadSequence = 0
    _isCompaniesLoading = NO
    _manualCompanyMetricsSynchronizationPromise: Promise<void> = Promise.resolve()
    _additionalSourceCPVCodes: string[] = []
    _initialCustomizationSnapshot?: string
    _inviteCountCache = new RHCPVInviteCountCache()
    _cpvCodesWithPendingCompanyCountRefresh = new Set<string>()
    _supplierCircleLocked = NO
    _canOverrideSourceCPVs = NO
    _persistsCompanyCustomization = YES

    /**
     * Called whenever the number of invited companies changes.
     * Argument is the count of companies with isInvited === true.
     */
    invitedCountDidChange?: (count: number) => void

    override _focusManager = new RHFocusManager(() => [
        this.sourceCodesButton,
        ...this.invitedCompaniesView._focusManager!.focusableItems(),
        this.confirmButton
    ])

    constructor(elementID?: string) {
        super(elementID)
        this.view = this.contentView
        this.closeButton.configureWithObject({
            titleLabel: {
                text: RHButtons.dialogCloseTitle,
                style: {
                    fontSize: RHButtons.dialogCloseFontSize,
                    lineHeight: RHButtons.dialogCloseLineHeight
                }
            },
            variant: RHButtons.dialogCloseVariant,
            contentPadding: RHButtons.dialogCloseContentPadding,
            style: {
                borderRadius: RHButtons.dialogCloseBorderRadius
            }
        })
        this.dismissesOnTapOutside = YES
        this.controlEventTargetAccumulator.EscDown = (_sender, event) => {
            if (event.defaultPrevented) {
                return
            }
            event.preventDefault()
            this.dismiss()
        }

        this.invitedCompaniesView.setupDescriptors()
        this.invitedCompaniesView.inviteCountCache = this._inviteCountCache
        this.sourceCodesTreeDialog.foundCompanyCountForCPVCode = (cpvCode, shouldLoad) =>
            this._foundCompanyCountForCPVCode(cpvCode, shouldLoad)
        this.sourceCodesTreeDialog.inviteCountForCPVCode = (cpvCode, shouldLoad) =>
            this._inviteCountForCPVCode(cpvCode, shouldLoad)

        this.invitedCompaniesView.companiesDidChange = () => {
            this._updateInvitedCountLabel()
            this.invitedCountDidChange?.(this.invitedCompaniesView.selectedCount)
        }
        this.invitedCompaniesView.manualCompaniesDidChange = () => {
            this._scheduleManualCompanyMetricsSynchronization()
        }
        this._updateSourceCodesSummary()
        this._updateInvitedCountLabel()
    }

    get inviteCountCache(): RHCPVInviteCountCache {
        return this._inviteCountCache
    }

    set inviteCountCache(cache: RHCPVInviteCountCache) {
        this._inviteCountCache = cache
        this.invitedCompaniesView.inviteCountCache = cache
    }

    // ─── Forwarded API (kept for ProcurementPartEditorView compatibility) ─────

    get selectedCount(): number {
        return this.invitedCompaniesView.selectedCount
    }

    _seedCompanies(companies: InvitedCompany[], cpvCode?: string) {
        this.invitedCompaniesView.seedIfEmpty(companies, cpvCode)
        this._updateInvitedCountLabel()
    }

    // ─── Show / dismiss ───────────────────────────────────────────────────────

    async show(
        cpvCode?: string,
        additionalSourceCPVCodes: string[] = [],
        onSave?: (
            invitedCompanies: InvitedCompany[],
            additionalSourceCPVCodes: string[]
        ) => void,
        options: {
            supplierCircleLocked?: boolean
            canOverrideSourceCPVs?: boolean
            persistsCompanyCustomization?: boolean
        } = {}
    ) {
        const loadSequence = ++this._loadSequence
        const sourceReloadSequence = ++this._sourceReloadSequence
        this._cpvCode = cpvCode
        this._onSaveConfirmed = onSave
        this._supplierCircleLocked = options.supplierCircleLocked ?? NO
        this._canOverrideSourceCPVs = options.canOverrideSourceCPVs ?? NO
        this._persistsCompanyCustomization = options.persistsCompanyCustomization ?? YES
        this.invitedCompaniesView._setManagementControlsVisible(!this._supplierCircleLocked)
        this._initialCustomizationSnapshot = undefined
        this._additionalSourceCPVCodes =
            ProcurementInvitationState.normalizedAdditionalSourceCPVCodes(
                cpvCode,
                additionalSourceCPVCodes
            )
        this._updateSourceCodesSummary()

        this.invitedCompaniesView.companiesDataView.tableView._clearKeyboardFocus()

        if (cpvCode) {
            // Clear any seeded rows BEFORE the first layout. The seeded list can
            // contain dozens of variable-height rows whose synchronous
            // measurement blocks the main thread for ~2s, so the dialog and its
            // loading spinner never paint (the screen appears frozen). The data
            // setter filters asynchronously, so we await the clear here; with an
            // empty table the first layout is cheap, the dialog shell and the
            // spinner paint immediately, and loadForCPVCode repopulates the rows
            // afterwards while the spinner is visible.
            await this.invitedCompaniesView.showLoadingPlaceholder()
            this._applyCompaniesLoadingState(YES)
        }

        this.showInRootView(YES)
        this.layoutIfNeeded()
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager)

        if (cpvCode) {
            // Yield one paint so the dialog shell and the spinner are actually
            // rendered before the heavy row measurement runs.
            await this._waitForNextPaint()
            try {
                const didLoad = await this._reloadCompaniesForCurrentSourceCodes()
                if (
                    !didLoad &&
                    loadSequence === this._loadSequence &&
                    sourceReloadSequence === this._sourceReloadSequence
                ) {
                    this._cpvCode = undefined
                }
                else if (
                    didLoad &&
                    loadSequence === this._loadSequence &&
                    sourceReloadSequence === this._sourceReloadSequence
                ) {
                    this._initialCustomizationSnapshot = JSON.stringify(
                        this._customizationSnapshot(cpvCode)
                    )
                }
                // Yield again so the freshly loaded rows lay out while the
                // spinner is still visible, instead of after it is removed.
                await this._waitForNextPaint()
            }
            finally {
                if (
                    loadSequence === this._loadSequence &&
                    sourceReloadSequence === this._sourceReloadSequence
                ) {
                    this._applyCompaniesLoadingState(NO)
                }
            }
        }

        if (
            loadSequence === this._loadSequence &&
            sourceReloadSequence === this._sourceReloadSequence &&
            this.contentView.isMemberOfViewTree
        ) {
            this.invitedCompaniesView.companiesDataView.searchTextField.focus()
        }
    }

    override dismiss() {
        const wasInitialLoadInProgress =
            this._isCompaniesLoading && this._initialCustomizationSnapshot === undefined
        this._loadSequence++
        this._sourceReloadSequence++
        this.invitedCompaniesView.invalidatePendingSourceLoads()
        this._applyCompaniesLoadingState(NO)
        RHPageFocusManager.shared.popLayer()

        if (wasInitialLoadInProgress) {
            this._cpvCode = undefined
            this._onSaveConfirmed = undefined
        }
        else {
            this._finalizeInvitedCompanySelection()
        }

        super.dismiss()
    }

    // ─── Persistence ──────────────────────────────────────────────────────────

    /**
     * Persists only the base CPV customization rows (plus companies added in
     * this dialog) and returns the selected procurement snapshot to the editor.
     * Safe to call multiple times — _cpvCode is cleared after the first call.
     */
    _applyCompaniesLoadingState(loading: boolean) {
        this._isCompaniesLoading = loading
        this.invitedCompaniesView.companiesDataView.loading = loading
    }

    /** Resolves after the browser has had a chance to paint a frame, so a
     *  just-shown spinner is visible before the next blocking layout runs. */
    _waitForNextPaint(): Promise<void> {
        return new Promise((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
        })
    }

    _openSourceCodesPicker() {
        const baseCPVCode = this._cpvCode
        if (!baseCPVCode || (this._supplierCircleLocked && !this._canOverrideSourceCPVs)) {
            return
        }

        this.sourceCodesTreeDialog.configureWithObject({
            dialogTitle: "Vali täiendavad CPV-allikad",
            confirmButtonTitle: "Rakenda valik",
            cancelButtonTitle: "Tühista",
            confirmsSelectionOnDismiss: NO,
            allowsMultipleSelection: YES,
            selectionExpansionMode: "none",
            showsAncestorSelectionState: NO,
            lockedSelectedCodes: [baseCPVCode]
        })

        const initialSelectedCodes = [...this._additionalSourceCPVCodes]
        if (!initialSelectedCodes.includes(baseCPVCode)) {
            initialSelectedCodes.push(baseCPVCode)
        }

        void this.sourceCodesTreeDialog.show(
            initialSelectedCodes,
            selectedCodes => {
                this._additionalSourceCPVCodes = this._additionalSourceCodesFromSelectedCodes(selectedCodes)
                this._updateSourceCodesSummary()
                void this._reloadSourceCompaniesAfterSelectionChange()
            },
            () => {
                this.sourceCodesButton.focus()
            }
        )
    }


    _additionalSourceCodesFromSelectedCodes(selectedCodes: string[]): string[] {
        return ProcurementInvitationState.normalizedAdditionalSourceCPVCodes(
            this._cpvCode,
            selectedCodes
        )
    }


    async _reloadSourceCompaniesAfterSelectionChange() {
        const loadSequence = this._loadSequence
        const sourceReloadSequence = ++this._sourceReloadSequence
        this.invitedCompaniesView.selectionSnapshotIsAuthoritative = NO
        this._applyCompaniesLoadingState(YES)
        try {
            await this._waitForNextPaint()
            await this._reloadCompaniesForCurrentSourceCodes(NO)
            await this._waitForNextPaint()
        }
        finally {
            if (
                loadSequence === this._loadSequence &&
                sourceReloadSequence === this._sourceReloadSequence
            ) {
                this._applyCompaniesLoadingState(NO)
            }
        }
    }


    async _reloadCompaniesForCurrentSourceCodes(
        selectionSnapshotIsAuthoritative =
            this.invitedCompaniesView.selectionSnapshotIsAuthoritative
    ): Promise<boolean> {
        const loadSequence = this._loadSequence
        const sourceReloadSequence = this._sourceReloadSequence
        const sourceCPVCodes = this._activeSourceCPVCodes()
        if (sourceCPVCodes.length === 0) {
            return NO
        }

        const companiesToEnrich = this.invitedCompaniesView.allCompanies
        const [availableFinancialsAndTrackRecord] = await Promise.all([
            RHCore.sharedInstance.procurementStore.retrieveInvitedCompanyFinancialsAndTrackRecordForCPVCodes(
                sourceCPVCodes,
                companiesToEnrich
            ),
            ...sourceCPVCodes.map(cpvCode =>
                this._inviteCountCache.reloadCompaniesForCPVCode(cpvCode)
            )
        ])
        if (
            loadSequence !== this._loadSequence ||
            sourceReloadSequence !== this._sourceReloadSequence
        ) {
            return NO
        }

        const didLoad = await this.invitedCompaniesView.loadForCPVCodes(
            sourceCPVCodes,
            selectionSnapshotIsAuthoritative,
            availableFinancialsAndTrackRecord
        )
        this._updateSourceCodesSummary()
        return didLoad
    }


    _scheduleManualCompanyMetricsSynchronization() {
        const cpvCode = this._cpvCode
        const loadSequence = this._loadSequence
        if (!cpvCode || !this._persistsCompanyCustomization) {
            return
        }

        this._manualCompanyMetricsSynchronizationPromise =
            this._manualCompanyMetricsSynchronizationPromise.then(() =>
                this._synchronizeManualCompanyMetricsForCPVCode(cpvCode, loadSequence)
            ).catch(error => {
                console.error("Error loading manually added company metrics:", error)
            })
    }


    async _synchronizeManualCompanyMetricsForCPVCode(cpvCode: string, loadSequence: number) {
        if (loadSequence !== this._loadSequence || cpvCode !== this._cpvCode) {
            return
        }

        const sourceReloadSequence = ++this._sourceReloadSequence
        const snapshot = this._customizationSnapshot(cpvCode)
        this._applyCompaniesLoadingState(YES)
        try {
            await this._waitForNextPaint()
            await RHCore.sharedInstance.procurementStore
                .saveInvitedCompanyCustomizationForCPVCode(cpvCode, snapshot)
            if (
                loadSequence !== this._loadSequence ||
                sourceReloadSequence !== this._sourceReloadSequence ||
                cpvCode !== this._cpvCode
            ) {
                return
            }

            const didLoad = await this._reloadCompaniesForCurrentSourceCodes()
            if (
                didLoad &&
                loadSequence === this._loadSequence &&
                sourceReloadSequence === this._sourceReloadSequence &&
                cpvCode === this._cpvCode
            ) {
                this._initialCustomizationSnapshot = JSON.stringify(snapshot)
                await this._waitForNextPaint()
            }
        }
        finally {
            if (
                loadSequence === this._loadSequence &&
                sourceReloadSequence === this._sourceReloadSequence
            ) {
                this._applyCompaniesLoadingState(NO)
            }
        }
    }

    _activeSourceCPVCodes(): string[] {
        const cpvCodes: string[] = []
        if (this._cpvCode) {
            cpvCodes.push(this._cpvCode)
        }
        for (const sourceCode of this._additionalSourceCPVCodes) {
            if (!cpvCodes.includes(sourceCode)) {
                cpvCodes.push(sourceCode)
            }
        }
        return cpvCodes
    }


    _inviteCountForCPVCode(cpvCode: string, shouldLoad = YES): number | undefined {
        const cachedCount = this._inviteCountCache.cachedInvitedCompanyCountForCPVCode(cpvCode)
        if (cachedCount !== undefined) {
            return cachedCount
        }

        this._scheduleCompanyCountRefresh(cpvCode, shouldLoad)
        return undefined
    }


    _foundCompanyCountForCPVCode(cpvCode: string, shouldLoad = YES): number | undefined {
        const cachedCount = this._inviteCountCache.cachedFoundCompanyCountForCPVCode(cpvCode)
        if (cachedCount !== undefined) {
            return cachedCount
        }

        this._scheduleCompanyCountRefresh(cpvCode, shouldLoad)
        return undefined
    }


    _scheduleCompanyCountRefresh(cpvCode: string, shouldLoad: boolean) {
        if (shouldLoad == NO || this._cpvCodesWithPendingCompanyCountRefresh.has(cpvCode)) {
            return
        }
        this._cpvCodesWithPendingCompanyCountRefresh.add(cpvCode)

        void this._inviteCountCache.retrieveFoundCompanyCountForCPVCode(cpvCode).then(() => {
            this.sourceCodesTreeDialog.refreshDisplayedRows()
        }).catch(() => {
            // Count suffixes are best-effort; the table load path surfaces real errors.
        }).finally(() => {
            this._cpvCodesWithPendingCompanyCountRefresh.delete(cpvCode)
        })
    }


    _updateSourceCodesSummary() {
        this.primaryCPVCodeLabel.text = this._cpvCode ?? "Valimata"
        this.sourceCodesLabel.text = "Täiendavad CPV-allikad:"
        if (!this._cpvCode) {
            this.sourceCodesSummaryLabel.text = "Vali esmalt CPV-kood."
            this.sourceCodesButton.enabled = NO
            this.sourceCodesButton.titleLabel.text = "Vali täiendavad CPV-allikad"
            return
        }

        this.sourceCodesButton.enabled = !this._supplierCircleLocked || this._canOverrideSourceCPVs
        if (this._additionalSourceCPVCodes.length === 0) {
            this.sourceCodesSummaryLabel.text = "Täiendavaid CPV-allikaid pole valitud."
            this.sourceCodesButton.titleLabel.text = "Vali täiendavad CPV-allikad"
        }
        else {
            this.sourceCodesLabel.text = `Täiendavad CPV-allikad (${this._additionalSourceCPVCodes.length}):`
            this.sourceCodesSummaryLabel.text = this._additionalSourceCPVCodes.join(", ")
            this.sourceCodesButton.titleLabel.text = `Vali täiendavad CPV-allikad (${this._additionalSourceCPVCodes.length})`
        }
        this.sourceCodesButton.setNeedsLayout()
        this.setNeedsLayout()
    }


    _updateInvitedCountLabel() {
        const invitedCount = this.invitedCompaniesView.selectedCount
        this.invitedCountLabel.configureWithObject({
            text: `Kutsutud: ${invitedCount}`
        })
        this.invitedCountLabel.setNeedsLayout()
        this.setNeedsLayout()
    }

    _finalizeInvitedCompanySelection() {
        if (!this._cpvCode) {
            return
        }
        const cpvCode = this._cpvCode
        this._cpvCode = undefined

        // Stamp cpvCodes onto every company in the in-memory list so that
        // allCompanies() returns objects with correct cpvCodes. This is the
        // canonical write-back point — _companies is the source of truth for
        // everything downstream (ProcurementPartsEditorView.invitedCompanies,
        // the preview column, and the procurement-level snapshot sent to saveDraft).
        for (const company of this.invitedCompaniesView._companies) {
            company.cpvCodes = [cpvCode]
        }

        const customizationCompanies =
            this.invitedCompaniesView.companiesForSourceCustomization(cpvCode)
        const snapshot = this._customizationSnapshot(cpvCode)

        if (
            this._persistsCompanyCustomization &&
            JSON.stringify(snapshot) !== this._initialCustomizationSnapshot
        ) {
            this._inviteCountCache.replaceCompaniesForCPVCode(
                cpvCode,
                customizationCompanies
            )
            this._manualCompanyMetricsSynchronizationPromise =
                this._manualCompanyMetricsSynchronizationPromise.then(() =>
                    RHCore.sharedInstance.procurementStore
                        .saveInvitedCompanyCustomizationForCPVCode(cpvCode, snapshot)
                )
                .catch(error => {
                    console.error("Error saving invited companies on dismiss:", error)
                })
        }

        this._onSaveConfirmed?.(
            this.invitedCompaniesView.invitedCompanies,
            [...this._additionalSourceCPVCodes]
        )
        this._onSaveConfirmed = undefined
    }


    _customizationSnapshot(cpvCode: string) {
        return this.invitedCompaniesView.companiesForSourceCustomization(cpvCode)
            .map(company => ({
                name: company.name,
                registryCode: company.registryCode,
                email: company.email,
                isInvited: company.isInvited,
                sources: [...company.sources].sort()
            }))
            .sort((firstCompany, secondCompany) => {
                const firstIdentity = firstCompany.registryCode ??
                                      firstCompany.email ??
                                      firstCompany.name
                const secondIdentity = secondCompany.registryCode ??
                                       secondCompany.email ??
                                       secondCompany.name
                return firstIdentity.localeCompare(secondIdentity)
            })
    }

    // ─── Layout ───────────────────────────────────────────────────────────────

    override layoutSubviews() {
        super.layoutSubviews()

        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const dialogBounds = this.contentBounds
        const closeButtonSize = RHButtons.dialogCloseSize
        let sourceCodesButtonWidth = this.sourceCodesButton.titleLabel.intrinsicContentWidth(
            RHButtons.compactPrimaryHeight
        ) + RHButtons.compactPrimaryContentPadding * 2 + padding

        sourceCodesButtonWidth = Math.max(padding * 10, sourceCodesButtonWidth)
        sourceCodesButtonWidth = Math.min(padding * 18, sourceCodesButtonWidth)

        let invitedCountLabelWidth = this.invitedCountLabel.intrinsicContentWidth(labelHeight) + padding
        invitedCountLabelWidth = Math.max(padding * 8, invitedCountLabelWidth)
        invitedCountLabelWidth = Math.min(padding * 14, invitedCountLabelWidth)

        const dialogWidth = Math.min(
            RHInvitedCompaniesViewTokens.dialogMaxContentWidth,
            dialogBounds.width * 0.95
        )

        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(0)

        const bounds = this.contentView.contentBoundsWithInset(padding * 1.5)
        const headerRowFrame = bounds.rectangleWithHeight(labelHeight * 1.5)
        headerRowFrame.rectangleWithWidth(headerRowFrame.width - closeButtonSize - padding)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextColumn(padding, closeButtonSize)
            .rectangleWithHeight(closeButtonSize, 0.5)
            .assignedAsFrameOfView(this.closeButton)

        headerRowFrame
            .rectangleForNextRow(padding * 0.5, labelHeight * 0.85)
            .assignedAsFrameOfView(this.primaryCPVCodeTitleLabel)
            .rectangleForNextRow(padding * 0.25, labelHeight)
            .assignedAsFrameOfView(this.primaryCPVCodeLabel)
            .rectangleForNextRow(padding * 0.5, labelHeight * 0.85)
            .distributeViewsAlongWidth(
                [this.sourceCodesLabel, this.invitedCountLabel],
                undefined,
                padding,
                [nil, invitedCountLabelWidth]
            )
            .rectangleForNextRow(padding * 0.25, labelHeight)
            .distributeViewsAlongWidth(
                [this.sourceCodesSummaryLabel, this.sourceCodesButton],
                undefined,
                padding,
                [nil, sourceCodesButtonWidth]
            )
            .rectangleForNextRow(padding, this.invitedCompaniesView)
            .assignedAsFrameOfView(this.invitedCompaniesView)
            .rectangleForNextRow(padding * 0.75, labelHeight)
            .rectangleWithWidth(padding * 10, 0.5)
            .assignedAsFrameOfView(this.confirmButton)

        const finalHeight = this.confirmButton.frame.max.y + padding * 1.5

        // Center when the dialog fits the viewport; when its natural content is
        // taller than that, rectangleWithHeight(finalHeight, 0.5) would push the
        // top edge above y = 0 (unreachable — the backdrop can't scroll to a
        // negative offset) with no padding at either edge. Clamp the top instead
        // and let the backdrop's own scroll reach the rest, as intended.
        let dialogFrame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(finalHeight, 0.5)

        if (dialogFrame.y < padding) {
            dialogFrame = dialogFrame.rectangleWithY(padding)
        }

        this.contentView.frame = dialogFrame

        // UIView positions children via CSS transform (translate3d), not via
        // top/left, so a child's footprint inside an overflow:auto parent comes
        // only from its own CSS height — never from where its transform visually
        // places it. To make the backdrop's scrollable area actually reach
        // `padding` past contentView's bottom edge, the spacer's height itself
        // (not its y) must extend that far.
        this.bottomSpacerView.frame = dialogBounds.rectangleWithHeight(dialogFrame.max.y + padding)
    }

}
